import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  Scan,
  CheckCircle,
  Trophy,
  Heart,
  MessageCircle,
  Share2,
  Users,
  Zap,
  Clock,
  Target,
  Sparkles,
  ArrowRight,
  Flame,
  Crown,
  Wifi,
  X,
  Rocket,
  TrendingUp,
  Newspaper,
  BarChart3,
  Radio,
  Home,
  Search,
  User,
  ThumbsUp,
  LayoutGrid,
} from "lucide-react";
import { Button } from "../components/common/Button";
import Logo from "../components/common/Logo";

// ============================================
// ANIMATED BACKGROUND COMPONENTS
// ============================================

const FloatingParticles: React.FC = () => {
  const particles = Array.from({ length: 20 });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: `rgba(34, 197, 94, ${Math.random() * 0.3 + 0.1})`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: Math.random() * 4 + 3,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

const GlowingOrbs: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)",
          left: "-10%",
          top: "-20%",
        }}
        animate={{
          x: [0, 50, 0],
          y: [0, 30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
          right: "-10%",
          bottom: "-10%",
        }}
        animate={{
          x: [0, -40, 0],
          y: [0, -20, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
    </div>
  );
};

const Confetti: React.FC<{ count?: number }> = ({ count = 150 }) => {
  const colors = ["#22C55E", "#FFD700", "#EF4444", "#3B82F6", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4"];

  return (
    <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const isLeft = Math.random() > 0.5;
        return (
          <motion.div
            key={i}
            className="absolute"
            style={{
              width: Math.random() * 10 + 5,
              height: Math.random() * 10 + 5,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              left: isLeft ? `${Math.random() * 30}%` : `${70 + Math.random() * 30}%`,
              backgroundColor: colors[i % colors.length],
            }}
            initial={{
              top: "50%",
              opacity: 1,
              rotate: 0,
              scale: 0,
            }}
            animate={{
              top: ["50%", `${-20 + Math.random() * 140}%`],
              left: isLeft
                ? [`${Math.random() * 30}%`, `${20 + Math.random() * 60}%`]
                : [`${70 + Math.random() * 30}%`, `${20 + Math.random() * 60}%`],
              opacity: [1, 1, 0],
              rotate: [0, Math.random() * 720 - 360],
              scale: [0, 1, 0.5],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              delay: Math.random() * 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        );
      })}
    </div>
  );
};

const PulsingRing: React.FC<{ size?: number; color?: string }> = ({ size = 200, color = "#22C55E" }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border-2"
          style={{
            width: size,
            height: size,
            borderColor: color,
            opacity: 0.3 - i * 0.1,
          }}
          animate={{
            scale: [1, 1.5 + i * 0.3, 1],
            opacity: [0.3 - i * 0.1, 0, 0.3 - i * 0.1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.4,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// SIMULATION CONFIG
// ============================================

type SimulationStage =
  | "intro"
  | "uploading"
  | "analyzing"
  | "extracting"
  | "verifying"
  | "posting"
  | "live_intro"
  | "highlight_tracking"
  | "live_tracking"
  | "highlight_sweatroom"
  | "live_sweatroom"
  | "highlight_news"
  | "live_news"
  | "highlight_stats"
  | "live_stats"
  | "winning"
  | "celebration"
  | "joinEarly";

// PICKS data
const mockPicksData = {
  user: {
    username: "demo_user",
    reputation: 87,
    streak: 5,
  },
  picks: [
    { team: "Chiefs", opponent: "Raiders", pick: "Chiefs to Win", status: "pending" },
    { team: "Lakers", opponent: "Celtics", pick: "Lakers to Win", status: "pending" },
    { team: "Yankees", opponent: "Red Sox", pick: "Over Total Runs", status: "pending" },
  ],
};

const mockChatMessages = [
  { user: "@parlay_king", msg: "Let's gooo! 🔥", delay: 500 },
  { user: "@sports_fan_22", msg: "Chiefs looking strong!", delay: 1500 },
  { user: "@winner_circle", msg: "Tailing this one 💰", delay: 2500 },
  { user: "@clutch_bettor", msg: "This parlay is fire 🔥", delay: 3500 },
];

const mockNewsHeadlines = [
  { headline: "🏈 Chiefs RB cleared to play tonight", delay: 500 },
  { headline: "🏀 Lakers announce starting lineup change", delay: 1500 },
  { headline: "⚾ Yankees ace confirmed for tonight", delay: 2500 },
];

const stageConfig: Record<SimulationStage, { title: string; subtitle: string; duration: number }> = {
  intro: { title: "", subtitle: "", duration: 0 },
  uploading: { title: "Uploading Screenshot", subtitle: "Securely uploading your slip...", duration: 2000 },
  analyzing: { title: "AI Analysis", subtitle: "Scanning with advanced OCR...", duration: 2500 },
  extracting: { title: "Extracting Picks", subtitle: "Identifying your selections...", duration: 2500 },
  verifying: { title: "Verifying Picks", subtitle: "Cross-referencing with live games...", duration: 2000 },
  posting: { title: "Going Live", subtitle: "Publishing to the community...", duration: 1500 },
  live_intro: { title: "Your Picks are LIVE!", subtitle: "", duration: 2000 },
  highlight_tracking: { title: "", subtitle: "", duration: 0 },
  live_tracking: { title: "Your Picks are LIVE!", subtitle: "", duration: 5000 },
  highlight_sweatroom: { title: "", subtitle: "", duration: 0 },
  live_sweatroom: { title: "Your Picks are LIVE!", subtitle: "", duration: 5000 },
  highlight_news: { title: "", subtitle: "", duration: 0 },
  live_news: { title: "Your Picks are LIVE!", subtitle: "", duration: 5000 },
  highlight_stats: { title: "", subtitle: "", duration: 0 },
  live_stats: { title: "Your Picks are LIVE!", subtitle: "", duration: 4000 },
  winning: { title: "Final Pick...", subtitle: "The moment of truth", duration: 3000 },
  celebration: { title: "ALL PICKS HIT! 🔥", subtitle: "Your streak continues!", duration: 4000 },
  joinEarly: { title: "", subtitle: "", duration: 0 },
};

// ============================================
// COMMUNITY FEED SIMULATOR COMPONENT
// ============================================

type SimulatorView = "tracker" | "community";

const CommunityFeedSimulator: React.FC<{ onClose: () => void; onSwitchView: () => void }> = ({ onClose, onSwitchView }) => {
  const [activeTab, setActiveTab] = useState<"news" | "community">("community");
  const [visiblePosts, setVisiblePosts] = useState<number[]>([1, 2]);
  const [visibleNews, setVisibleNews] = useState<number[]>([1, 2]);
  const [slipStatus, setSlipStatus] = useState({ pick1: "winning", pick2: "pending", score1: "24-17", score2: "Q2 8:45" });
  const [sweatRoomCount, setSweatRoomCount] = useState(847);
  const [gameTime, setGameTime] = useState("Q4 2:34");
  const [likeBumps, setLikeBumps] = useState<Record<number, number>>({});

  // All community posts
  const allCommunityPosts = [
    { id: 1, user: "ChiefsKingdom", avatar: "CK", time: "just now", content: "Chiefs ML looking STRONG! 🔥 Who's sweating with me?", likes: 47, replies: 12, sport: "NFL" },
    { id: 2, user: "HoopsDreams", avatar: "HD", time: "1m ago", content: "Lakers -4.5 was the play. LeBron activated. 👑", likes: 89, replies: 23, sport: "NBA" },
    { id: 3, user: "BronxBomber", avatar: "BB", time: "2m ago", content: "Yankees over 8.5 runs ✅ Judge is on fire!", likes: 156, replies: 41, sport: "MLB", isHit: true },
    { id: 4, user: "GridironGuru", avatar: "GG", time: "just now", content: "TOUCHDOWN CHIEFS! 🏈 This parlay is ALIVE!", likes: 234, replies: 67, sport: "NFL", isNew: true },
    { id: 5, user: "CourtKing", avatar: "CK", time: "just now", content: "AD with the block! Lakers defense is ELITE tonight 🛡️", likes: 112, replies: 28, sport: "NBA", isNew: true },
  ];

  // All news items
  const allNewsItems = [
    { id: 1, headline: "Mahomes leads Chiefs to 4th quarter comeback", source: "ESPN", time: "LIVE", sport: "NFL", isLive: true },
    { id: 2, headline: "Lakers injury update: Davis expected to play tonight", source: "The Athletic", time: "12m ago", sport: "NBA" },
    { id: 3, headline: "Trade deadline: Yankees acquire All-Star closer", source: "MLB Network", time: "1h ago", sport: "MLB" },
    { id: 4, headline: "BREAKING: Chiefs score go-ahead touchdown in Q4", source: "NFL Network", time: "LIVE", sport: "NFL", isLive: true, isNew: true },
    { id: 5, headline: "Lakers take 8-point lead entering 4th quarter", source: "ESPN", time: "LIVE", sport: "NBA", isLive: true, isNew: true },
  ];

  // Auto-cycle tabs
  useEffect(() => {
    const tabInterval = setInterval(() => setActiveTab(prev => prev === "community" ? "news" : "community"), 6000);
    return () => clearInterval(tabInterval);
  }, []);

  // Animate posts appearing
  useEffect(() => {
    const addPost = (delay: number, id: number) => {
      setTimeout(() => setVisiblePosts(prev => {
        if (prev.includes(id)) return prev;
        const newPosts = [...prev, id];
        if (newPosts.length > 3) newPosts.shift();
        return newPosts;
      }), delay);
    };
    addPost(3000, 3);
    addPost(7000, 4);
    addPost(11000, 5);
    const loop = setInterval(() => {
      setVisiblePosts([1, 2]);
      addPost(3000, 3);
      addPost(7000, 4);
      addPost(11000, 5);
    }, 18000);
    return () => clearInterval(loop);
  }, []);

  // Animate news appearing
  useEffect(() => {
    const addNews = (delay: number, id: number) => {
      setTimeout(() => setVisibleNews(prev => {
        if (prev.includes(id)) return prev;
        const newNews = [...prev, id];
        if (newNews.length > 3) newNews.shift();
        return newNews;
      }), delay);
    };
    addNews(2500, 3);
    addNews(6000, 4);
    addNews(9500, 5);
    const loop = setInterval(() => {
      setVisibleNews([1, 2]);
      addNews(2500, 3);
      addNews(6000, 4);
      addNews(9500, 5);
    }, 15000);
    return () => clearInterval(loop);
  }, []);

  // Animate slip status
  useEffect(() => {
    const updates = [
      { pick1: "winning", pick2: "pending", score1: "24-17", score2: "Q2 8:45" },
      { pick1: "winning", pick2: "winning", score1: "31-17", score2: "Q3 11:00" },
      { pick1: "hit", pick2: "winning", score1: "Final", score2: "Q4 2:30" },
    ];
    let i = 0;
    const interval = setInterval(() => { setSlipStatus(updates[i]); i = (i + 1) % updates.length; }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate counts
  useEffect(() => {
    const countInterval = setInterval(() => setSweatRoomCount(prev => prev + Math.floor(Math.random() * 5) + 1), 2000);
    return () => clearInterval(countInterval);
  }, []);

  useEffect(() => {
    const times = ["Q4 2:34", "Q4 2:18", "Q4 1:55", "Q4 1:32", "FINAL"];
    let i = 0;
    const interval = setInterval(() => { setGameTime(times[i]); i = (i + 1) % times.length; }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const postId = Math.floor(Math.random() * 5) + 1;
      setLikeBumps(prev => ({ ...prev, [postId]: (prev[postId] || 0) + Math.floor(Math.random() * 3) + 1 }));
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const communityPosts = allCommunityPosts.filter(p => visiblePosts.includes(p.id)).map(p => ({ ...p, likes: p.likes + (likeBumps[p.id] || 0) }));
  const newsItems = allNewsItems.filter(n => visibleNews.includes(n.id));
  const activeSweatRoom = { name: "Chiefs vs Raiders", participants: sweatRoomCount, sport: "NFL", status: gameTime };
  const liveSlip = {
    picks: [
      { team: "Chiefs", line: "ML", status: slipStatus.pick1, score: slipStatus.score1 },
      { team: "Lakers", line: "-4.5", status: slipStatus.pick2, score: slipStatus.score2 },
    ],
    potential: "$450",
    odds: "+320",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 p-4 md:p-8 overflow-auto"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between mb-6">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex items-center gap-3"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full">
              <Users size={16} className="text-primary" />
              <span className="text-primary font-semibold text-sm">
                COMMUNITY FEED
              </span>
            </div>
          </motion.div>

          {/* View Toggle */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-2"
          >
            <button
              onClick={onSwitchView}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-colors text-sm font-medium text-white"
            >
              <LayoutGrid size={14} />
              Switch to Tracker
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X size={20} className="text-white/70" />
            </button>
          </motion.div>
        </div>

        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row items-start justify-center gap-6">
          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative mx-auto lg:mx-0"
            style={{ maxWidth: "320px", width: "100%" }}
          >
            {/* Glow Effect */}
            <div className="absolute -inset-6 bg-primary/15 blur-3xl rounded-full opacity-60" />

            {/* Phone Frame */}
            <div className="relative rounded-[2.5rem] p-2.5 bg-gray-900 shadow-2xl">
              {/* Phone Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-xl z-20" />

              {/* Screen */}
              <div
                className="relative rounded-[2rem] overflow-hidden bg-gray-950"
                style={{ aspectRatio: "9/19" }}
              >
                {/* App Content */}
                <div className="absolute inset-0 flex flex-col">
                  {/* Status Bar */}
                  <div className="h-6 bg-black" />

                  {/* Top Tab Bar */}
                  <div className="flex items-center bg-gray-900/80 backdrop-blur-sm border-b border-white/10">
                    <button
                      onClick={() => setActiveTab("community")}
                      className={`flex-1 py-2.5 text-xs font-semibold transition-colors relative ${
                        activeTab === "community"
                          ? "text-primary"
                          : "text-white/50"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Users size={12} />
                        Community
                      </div>
                      {activeTab === "community" && (
                        <motion.div
                          layoutId="demoActiveTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab("news")}
                      className={`flex-1 py-2.5 text-xs font-semibold transition-colors relative ${
                        activeTab === "news" ? "text-primary" : "text-white/50"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Newspaper size={12} />
                        News
                      </div>
                      {activeTab === "news" && (
                        <motion.div
                          layoutId="demoActiveTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        />
                      )}
                    </button>
                  </div>

                  {/* SweatRoom Banner */}
                  <div className="mx-2 mt-2 px-3 py-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Flame size={12} className="text-orange-500" />
                          <span className="text-white text-[10px] font-bold">
                            SWEATROOM
                          </span>
                        </div>
                        <span className="text-white/70 text-[10px]">
                          {activeSweatRoom.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/30 rounded-full">
                          <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-red-400 text-[9px] font-medium">
                            {activeSweatRoom.status}
                          </span>
                        </div>
                        <span className="text-white/50 text-[9px]">
                          {activeSweatRoom.participants} sweating
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Feed Content */}
                  <div className="flex-1 overflow-hidden px-2 py-2">
                    <AnimatePresence mode="wait">
                      {activeTab === "community" ? (
                        <motion.div
                          key="community"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="space-y-2"
                        >
                          {communityPosts.map((post, i) => (
                            <motion.div
                              key={post.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className={`p-2.5 rounded-xl border ${
                                post.isHit
                                  ? "bg-green-500/10 border-green-500/30"
                                  : "bg-white/5 border-white/10"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary text-[9px] font-bold">
                                    {post.avatar}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-white text-[10px] font-semibold truncate">
                                      {post.user}
                                    </span>
                                    <span className="text-white/30 text-[9px]">•</span>
                                    <span className="text-white/40 text-[9px]">
                                      {post.time}
                                    </span>
                                    <span className="px-1.5 py-0.5 bg-white/10 rounded text-[8px] text-white/60">
                                      {post.sport}
                                    </span>
                                  </div>
                                  <p className="text-white/80 text-[10px] mt-0.5 leading-relaxed">
                                    {post.content}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1.5">
                                    <div className="flex items-center gap-1 text-white/40">
                                      <ThumbsUp size={9} />
                                      <span className="text-[9px]">{post.likes}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-white/40">
                                      <MessageCircle size={9} />
                                      <span className="text-[9px]">{post.replies}</span>
                                    </div>
                                    <Share2 size={9} className="text-white/40" />
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      ) : (
                        <motion.div
                          key="news"
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          className="space-y-2"
                        >
                          {newsItems.map((item, i) => (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className={`p-2.5 rounded-xl border ${
                                item.isLive
                                  ? "bg-red-500/10 border-red-500/30"
                                  : "bg-white/5 border-white/10"
                              }`}
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                {item.isLive ? (
                                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/30 rounded-full">
                                    <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                                    <span className="text-red-400 text-[9px] font-bold">
                                      LIVE
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-white/40">
                                    <Clock size={9} />
                                    <span className="text-[9px]">{item.time}</span>
                                  </div>
                                )}
                                <span className="px-1.5 py-0.5 bg-white/10 rounded text-[8px] text-white/60">
                                  {item.sport}
                                </span>
                              </div>
                              <p className="text-white text-[10px] font-medium leading-snug">
                                {item.headline}
                              </p>
                              <p className="text-white/40 text-[9px] mt-1">
                                {item.source}
                              </p>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Bottom Navigation */}
                  <div className="bg-gray-900/95 backdrop-blur-sm border-t border-white/10 px-4 py-2 pb-4">
                    <div className="flex items-center justify-around">
                      {[
                        { icon: Home, label: "Home", active: true },
                        { icon: Search, label: "Explore", active: false },
                        { icon: Target, label: "Picks", active: false },
                        { icon: Flame, label: "Sweat", active: false },
                        { icon: User, label: "Profile", active: false },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className={`flex flex-col items-center gap-0.5 ${
                            item.active ? "text-primary" : "text-white/40"
                          }`}
                        >
                          <item.icon size={16} />
                          <span className="text-[8px] font-medium">
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Floating Digital Slip - Desktop */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="hidden lg:block w-full max-w-[280px]"
          >
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="relative"
            >
              {/* Glow */}
              <div className="absolute -inset-4 bg-primary/20 blur-2xl rounded-2xl" />

              {/* Slip Card */}
              <div className="relative p-4 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-primary/30 shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Target size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-white text-xs font-bold">
                        Your Live Slip
                      </p>
                      <p className="text-white/50 text-[10px]">2-Leg Parlay</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-green-500 text-[10px] font-medium">
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Picks */}
                <div className="space-y-2 mb-3">
                  {liveSlip.picks.map((pick, i) => (
                    <div
                      key={i}
                      className={`p-2.5 rounded-lg ${
                        pick.status === "winning"
                          ? "bg-green-500/10 border border-green-500/30"
                          : "bg-white/5 border border-white/10"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-xs font-semibold">
                            {pick.team} {pick.line}
                          </p>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-[10px] font-bold ${
                              pick.status === "winning"
                                ? "text-green-500"
                                : "text-white/60"
                            }`}
                          >
                            {pick.score}
                          </p>
                          {pick.status === "winning" && (
                            <p className="text-green-500/70 text-[9px]">
                              Winning
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Payout */}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <div>
                    <p className="text-white/50 text-[10px]">Potential</p>
                    <p className="text-primary text-lg font-black">
                      {liveSlip.potential}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/50 text-[10px]">Odds</p>
                    <p className="text-white text-sm font-bold">
                      {liveSlip.odds}
                    </p>
                  </div>
                </div>

                {/* SweatRoom Link */}
                <button className="w-full mt-3 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/30 rounded-lg transition-colors">
                  <div className="flex items-center justify-center gap-2">
                    <Flame size={14} className="text-primary" />
                    <span className="text-primary text-xs font-semibold">
                      Join SweatRoom
                    </span>
                    <span className="text-primary/60 text-[10px]">
                      847 sweating
                    </span>
                  </div>
                </button>
              </div>
            </motion.div>

            {/* Feature highlights below slip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-4 space-y-2"
            >
              {[
                { icon: Users, label: "Community reactions in real-time" },
                { icon: Newspaper, label: "Personalized sports news" },
                { icon: Flame, label: "Auto-join SweatRooms" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                >
                  <item.icon size={14} className="text-primary" />
                  <span className="text-white/70 text-xs">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Mobile Slip Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:hidden w-full max-w-[320px] mx-auto"
          >
            <div className="p-3 rounded-xl bg-gradient-to-r from-gray-900 to-gray-950 border border-primary/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Target size={18} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-bold">
                        Your Live Slip
                      </p>
                      <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 rounded-full">
                        <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-green-500 text-[9px] font-medium">
                          LIVE
                        </span>
                      </div>
                    </div>
                    <p className="text-white/50 text-xs">
                      2-Leg Parlay • {liveSlip.odds}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-primary text-lg font-black">
                    {liveSlip.potential}
                  </p>
                  <p className="text-white/40 text-[10px]">Potential</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// DEMO PAGE COMPONENT
// ============================================

export const DemoPage: React.FC = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<SimulationStage>("intro");
  const [progress, setProgress] = useState(0);
  const [likes, setLikes] = useState(0);
  const [comments, setComments] = useState(0);
  const [tails, setTails] = useState(0);
  const [picks, setPicks] = useState(mockPicksData.picks);
  const [showConfetti, setShowConfetti] = useState(false);
  const [chatMessages, setChatMessages] = useState<typeof mockChatMessages>([]);
  const [newsHeadlines, setNewsHeadlines] = useState<typeof mockNewsHeadlines>([]);
  const [streak, setStreak] = useState(mockPicksData.user.streak);
  const [highlightElement, setHighlightElement] = useState<"picks" | "chat" | "news" | "stats" | null>(null);
  const [simulatorView, setSimulatorView] = useState<SimulatorView>("tracker");
  const [tutorialComplete, setTutorialComplete] = useState(false);

  const isProcessing = ["uploading", "analyzing", "extracting", "verifying", "posting"].includes(stage);
  const isLivePhase = stage.startsWith("live_") || stage === "winning" || stage === "celebration";
  const isHighlightPhase = stage.startsWith("highlight_");

  // Stage progression
  const advanceStage = useCallback(() => {
    const stages: SimulationStage[] = [
      "uploading", "analyzing", "extracting", "verifying", "posting",
      "live_intro", "highlight_tracking", "live_tracking",
      "highlight_sweatroom", "live_sweatroom",
      "highlight_news", "live_news",
      "highlight_stats", "live_stats",
      "winning", "celebration", "joinEarly",
    ];
    const currentIndex = stages.indexOf(stage);
    if (currentIndex < stages.length - 1) {
      setStage(stages[currentIndex + 1]);
    }
  }, [stage]);

  // Auto-advance stages (only for timed stages)
  useEffect(() => {
    if (stage === "intro" || stage === "joinEarly" || isHighlightPhase) return;
    const config = stageConfig[stage];
    if (config.duration > 0) {
      const timer = setTimeout(advanceStage, config.duration);
      return () => clearTimeout(timer);
    }
  }, [stage, advanceStage, isHighlightPhase]);

  // Set highlight element based on stage
  useEffect(() => {
    if (stage === "live_tracking" || stage === "highlight_tracking") {
      setHighlightElement("picks");
    } else if (stage === "live_sweatroom" || stage === "highlight_sweatroom") {
      setHighlightElement("chat");
    } else if (stage === "live_news" || stage === "highlight_news") {
      setHighlightElement("news");
    } else if (stage === "live_stats" || stage === "highlight_stats") {
      setHighlightElement("stats");
    } else {
      setHighlightElement(null);
    }
  }, [stage]);

  // Progress animation
  useEffect(() => {
    if (!isProcessing) return;
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 1.2, 100));
    }, stageConfig[stage].duration / 85);
    return () => clearInterval(interval);
  }, [stage, isProcessing]);

  // Engagement simulation (slower)
  useEffect(() => {
    if (!isLivePhase) return;
    const likeInterval = setInterval(() => setLikes((prev) => prev + Math.floor(Math.random() * 3) + 1), 600);
    const commentInterval = setInterval(() => setComments((prev) => prev + 1), 1200);
    const tailInterval = setInterval(() => setTails((prev) => prev + 1), 1800);
    return () => {
      clearInterval(likeInterval);
      clearInterval(commentInterval);
      clearInterval(tailInterval);
    };
  }, [isLivePhase]);

  // Chat messages
  useEffect(() => {
    if (!isLivePhase) return;
    const timers = mockChatMessages.map((msg) =>
      setTimeout(() => setChatMessages((prev) => {
        if (prev.find(m => m.user === msg.user)) return prev;
        return [...prev, msg];
      }), msg.delay + 1000)
    );
    return () => timers.forEach(clearTimeout);
  }, [isLivePhase]);

  // News headlines
  useEffect(() => {
    if (!isLivePhase) return;
    const timers = mockNewsHeadlines.map((news) =>
      setTimeout(() => setNewsHeadlines((prev) => {
        if (prev.find(n => n.headline === news.headline)) return prev;
        return [...prev, news];
      }), news.delay + 1000)
    );
    return () => timers.forEach(clearTimeout);
  }, [isLivePhase]);

  // Pick updates during live phase
  useEffect(() => {
    if (stage === "live_tracking") {
      const timer = setTimeout(() => {
        setPicks((prev) => prev.map((p, i) => (i === 0 ? { ...p, status: "won" } : p)));
      }, 3000);
      return () => clearTimeout(timer);
    }
    if (stage === "live_sweatroom") {
      const timer = setTimeout(() => {
        setPicks((prev) => prev.map((p, i) => (i === 1 ? { ...p, status: "won" } : p)));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [stage]);

  // Final pick during winning phase
  useEffect(() => {
    if (stage !== "winning") return;
    const timer = setTimeout(() => {
      setPicks((prev) => prev.map((p, i) => (i === 2 ? { ...p, status: "won" } : p)));
    }, 2000);
    return () => clearTimeout(timer);
  }, [stage]);

  // Confetti and streak update on celebration
  useEffect(() => {
    if (stage === "celebration") {
      setShowConfetti(true);
      setStreak((prev) => prev + 1);
      setTutorialComplete(true);
    }
  }, [stage]);

  const startSimulation = () => setStage("uploading");

  const handleHighlightContinue = () => {
    advanceStage();
  };

  const handleJoinEarly = () => {
    navigate("/login");
  };

  const handleClose = () => {
    navigate("/");
  };

  const handleSwitchView = () => {
    setSimulatorView(simulatorView === "tracker" ? "community" : "tracker");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl overflow-hidden"
    >
      {/* Background Effects */}
      <GlowingOrbs />
      <FloatingParticles />
      {showConfetti && <Confetti count={200} />}

      {/* Close Button */}
      {stage !== "joinEarly" && !isHighlightPhase && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          onClick={handleClose}
          className="absolute top-6 right-6 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={24} className="text-white/70" />
        </motion.button>
      )}


      <AnimatePresence mode="wait">
        {/* Intro Screen */}
        {stage === "intro" && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center max-w-lg px-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="relative mb-8"
              >
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-primary via-green-400 to-emerald-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-primary/40">
                  <Upload size={48} className="text-white" />
                </div>
                <PulsingRing size={180} />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-5xl font-black text-white mb-4"
              >
                Experience ParlayGround
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-gray-400 mb-8"
              >
                See how one screenshot transforms into a complete live sports experience
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  variant="primary"
                  size="lg"
                  icon={<Rocket size={24} />}
                  onClick={startSimulation}
                  className="px-10 py-4 text-lg font-bold shadow-xl shadow-primary/40 hover:shadow-primary/60 transition-all hover:scale-105"
                >
                  Start Demo
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-gray-500"
              >
                {[
                  { icon: Target, label: "Live Tracking" },
                  { icon: Radio, label: "SweatRooms" },
                  { icon: Newspaper, label: "News Feed" },
                  { icon: BarChart3, label: "Your Stats" },
                ].map((item, i) => (
                  <motion.span
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full"
                  >
                    <item.icon size={14} className="text-primary" />
                    {item.label}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Processing Stages */}
        {isProcessing && (
          <motion.div
            key="processing"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center max-w-md px-6">
              <motion.div
                className="relative mb-8"
                animate={{
                  rotateY: stage === "analyzing" ? [0, 360] : 0,
                }}
                transition={{
                  duration: 2,
                  repeat: stage === "analyzing" ? Infinity : 0,
                  ease: "linear"
                }}
              >
                <div className="w-28 h-28 mx-auto bg-gradient-to-br from-primary/20 to-emerald-500/20 rounded-3xl flex items-center justify-center border border-primary/30 backdrop-blur-sm">
                  {stage === "uploading" && <Upload size={40} className="text-primary" />}
                  {stage === "analyzing" && <Scan size={40} className="text-primary" />}
                  {stage === "extracting" && <Target size={40} className="text-primary" />}
                  {stage === "verifying" && <CheckCircle size={40} className="text-primary" />}
                  {stage === "posting" && <Sparkles size={40} className="text-primary" />}
                </div>
                <PulsingRing size={160} />
              </motion.div>

              <motion.h2
                key={stage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-bold text-white mb-2"
              >
                {stageConfig[stage].title}
              </motion.h2>

              <motion.p
                key={`${stage}-sub`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-gray-400 mb-8"
              >
                {stageConfig[stage].subtitle}
              </motion.p>

              {/* Progress Bar */}
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary via-green-400 to-emerald-400"
                    style={{ width: `${progress}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">{Math.round(progress)}%</p>
              </div>

              {/* Picks Preview during extracting */}
              {stage === "extracting" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-left"
                >
                  {mockPicksData.picks.map((pick, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + i * 0.3 }}
                      className="flex justify-between py-2 text-sm"
                    >
                      <span className="text-gray-500">{pick.team} vs {pick.opponent}</span>
                      <span className="text-primary font-medium">{pick.pick}</span>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Live Phase - also show during highlight phases */}
        {(isLivePhase || isHighlightPhase) && simulatorView === "tracker" && (
          <motion.div
            key="live"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 p-4 md:p-8 overflow-auto"
          >
            <div className="max-w-6xl mx-auto h-full flex flex-col">
              {/* Status Header */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex items-center justify-center gap-4 mb-6"
              >
                <motion.div
                  className={`inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-lg ${
                    stage === "celebration" ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-black" :
                    stage === "winning" ? "bg-gradient-to-r from-yellow-500/80 to-orange-500/80 text-black" :
                    "bg-gradient-to-r from-primary to-emerald-500 text-white"
                  }`}
                  animate={stage === "winning" || stage === "celebration" ? {
                    scale: [1, 1.05, 1],
                  } : {}}
                  transition={{ duration: 0.5, repeat: stage === "winning" ? Infinity : 0 }}
                >
                  {stage === "celebration" ? <Trophy size={24} /> : stage === "winning" ? <Zap size={24} /> : <Wifi size={24} />}
                  {stage === "celebration" ? "ALL PICKS HIT! 🔥" : stage === "winning" ? "Final Pick..." : "Your Picks are LIVE!"}
                </motion.div>

                {/* View Toggle - only show after tutorial complete */}
                {tutorialComplete && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={handleSwitchView}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-colors text-sm font-medium text-white"
                  >
                    <Users size={14} />
                    Community View
                  </motion.button>
                )}
              </motion.div>

              {/* Main Grid */}
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
                {/* Main Picks Card - 7 cols */}
                <div className="lg:col-span-7 flex flex-col">
                  <motion.div
                    className={`h-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border overflow-hidden transition-all duration-300 ${
                      stage === "celebration" ? "border-yellow-500/50 shadow-xl shadow-yellow-500/20" :
                      highlightElement === "picks" ? "border-primary/50 shadow-xl shadow-primary/20 ring-2 ring-primary/30" :
                      "border-white/10"
                    }`}
                    animate={stage === "celebration" ? { scale: [1, 1.02, 1] } : {}}
                    transition={{ duration: 0.5, repeat: stage === "celebration" ? 2 : 0 }}
                  >
                    {/* Inline Feature Badge for Tracking */}
                    <AnimatePresence>
                      {stage === "highlight_tracking" && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="px-4 py-3 bg-gradient-to-r from-primary/20 to-emerald-500/20 border-b border-primary/30"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <motion.div
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                                className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center"
                              >
                                <Target size={20} className="text-primary" />
                              </motion.div>
                              <div>
                                <p className="text-primary font-bold text-sm">Live Pick Tracking</p>
                                <p className="text-gray-400 text-xs">Watching your picks update in real-time</p>
                              </div>
                            </div>
                            <motion.button
                              onClick={handleHighlightContinue}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="px-4 py-2 bg-primary text-black text-xs font-bold rounded-full flex items-center gap-1"
                            >
                              Continue <ArrowRight size={14} />
                            </motion.button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Post Header */}
                    <div className="p-4 border-b border-white/10 bg-white/5">
                      <div className="flex items-center gap-3">
                        <motion.div
                          className="w-12 h-12 bg-gradient-to-br from-primary to-green-400 rounded-full flex items-center justify-center text-white font-bold text-lg"
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          D
                        </motion.div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">@demo_user</span>
                            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-medium">
                              Rep: {mockPicksData.user.reputation}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <motion.span
                              animate={{ opacity: [1, 0.5, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                              className="w-2 h-2 bg-red-500 rounded-full"
                            />
                            Live now
                          </div>
                        </div>
                        <div className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium flex items-center gap-1">
                          <Flame size={14} />
                          {streak} Pick Streak
                        </div>
                      </div>
                    </div>

                    {/* Picks Content */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-primary/20 text-primary rounded-lg text-sm font-medium">
                            3-Leg Picks
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Community Confidence</div>
                          <div className="text-xl font-bold text-primary">87%</div>
                        </div>
                      </div>

                      {/* Picks List */}
                      <div className="space-y-2">
                        {picks.map((pick, index) => (
                          <motion.div
                            key={index}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2 + index * 0.1 }}
                            className={`p-3 rounded-xl border transition-all ${
                              pick.status === "won"
                                ? "bg-green-500/20 border-green-500/50"
                                : "bg-white/5 border-white/10"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-white text-sm">{pick.team} vs {pick.opponent}</div>
                                <div className="text-xs text-gray-400">{pick.pick}</div>
                              </div>
                              <AnimatePresence mode="wait">
                                {pick.status === "won" ? (
                                  <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    className="flex items-center gap-1 text-green-400 font-bold text-sm"
                                  >
                                    <CheckCircle size={18} />
                                    HIT
                                  </motion.div>
                                ) : (
                                  <motion.div
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className="flex items-center gap-1 text-yellow-500 text-xs"
                                  >
                                    <Clock size={14} />
                                    Live
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Stats highlight */}
                      {highlightElement === "stats" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl"
                        >
                          {/* Inline Feature Badge for Stats */}
                          {stage === "highlight_stats" && (
                            <motion.div
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex items-center justify-between mb-3 pb-3 border-b border-purple-500/30"
                            >
                              <div className="flex items-center gap-2">
                                <motion.div
                                  animate={{ scale: [1, 1.1, 1] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                  className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center"
                                >
                                  <BarChart3 size={16} className="text-purple-400" />
                                </motion.div>
                                <div>
                                  <p className="text-purple-400 font-bold text-xs">Your Stats Logged</p>
                                  <p className="text-gray-400 text-[10px]">Build your reputation with every pick</p>
                                </div>
                              </div>
                              <motion.button
                                onClick={handleHighlightContinue}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-3 py-1.5 bg-purple-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1"
                              >
                                Continue <ArrowRight size={12} />
                              </motion.button>
                            </motion.div>
                          )}
                          <div className="flex items-center gap-2 text-purple-400 font-semibold mb-2">
                            <BarChart3 size={18} />
                            Your Stats Updated
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <div className="text-2xl font-bold text-white">73%</div>
                              <div className="text-xs text-gray-400">Hit Rate</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-white">{streak}</div>
                              <div className="text-xs text-gray-400">Streak</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-white">142</div>
                              <div className="text-xs text-gray-400">Total Picks</div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Celebration Message */}
                      {stage === "celebration" && (
                        <motion.div
                          initial={{ scale: 0, y: 20 }}
                          animate={{ scale: 1, y: 0 }}
                          className="mt-4 p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl text-center"
                        >
                          <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold text-lg">
                            <Flame size={24} />
                            ALL PICKS HIT!
                            <Flame size={24} />
                          </div>
                          <div className="text-orange-300 text-sm mt-1">
                            Streak extended to {streak}! 🔥
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* Engagement Bar */}
                    <div className="px-4 py-3 bg-white/5 border-t border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5">
                          <motion.div
                            className="flex items-center gap-1.5 text-red-400"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.2 }}
                            key={likes}
                          >
                            <Heart size={18} fill="currentColor" />
                            <span className="font-medium">{likes}</span>
                          </motion.div>
                          <motion.div
                            className="flex items-center gap-1.5 text-blue-400"
                            key={comments}
                          >
                            <MessageCircle size={18} />
                            <span className="font-medium">{comments}</span>
                          </motion.div>
                          <Share2 size={18} className="text-gray-500" />
                        </div>
                        <motion.div
                          className="flex items-center gap-1.5 text-primary font-medium"
                          key={tails}
                        >
                          <Users size={18} />
                          <span>{tails} tailing</span>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Side Panels - 5 cols */}
                <div className="lg:col-span-5 flex flex-col gap-4 min-h-0">
                  {/* Live Chat / SweatRoom */}
                  <div className="flex-1 min-h-0">
                    <div className={`h-full bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-2xl border overflow-hidden flex flex-col transition-all duration-300 ${
                      highlightElement === "chat" ? "border-blue-500/50 shadow-xl shadow-blue-500/20 ring-2 ring-blue-500/30" : "border-blue-500/20"
                    }`}>
                      {/* Inline Feature Badge for SweatRoom */}
                      <AnimatePresence>
                        {stage === "highlight_sweatroom" && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="px-3 py-2.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-blue-500/30"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <motion.div
                                  animate={{ scale: [1, 1.1, 1] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                  className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center"
                                >
                                  <Radio size={16} className="text-blue-400" />
                                </motion.div>
                                <div>
                                  <p className="text-blue-400 font-bold text-xs">SweatRoom Connected</p>
                                  <p className="text-gray-400 text-[10px]">Live with fans watching the same games</p>
                                </div>
                              </div>
                              <motion.button
                                onClick={handleHighlightContinue}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-3 py-1.5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1"
                              >
                                Continue <ArrowRight size={12} />
                              </motion.button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="p-3 border-b border-white/10 bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Radio size={16} className="text-blue-400" />
                            <span className="font-semibold text-white text-sm">SweatRoom</span>
                          </div>
                          <motion.span
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="flex items-center gap-1 text-green-400 text-xs"
                          >
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                            47 watching
                          </motion.span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
                        <AnimatePresence>
                          {chatMessages.map((msg, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20, scale: 0.9 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              className="bg-white/5 rounded-lg p-2"
                            >
                              <span className="text-primary font-medium text-xs">{msg.user}</span>
                              <p className="text-gray-300 text-sm">{msg.msg}</p>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {chatMessages.length === 0 && (
                          <div className="flex items-center justify-center h-full">
                            <motion.div
                              animate={{ opacity: [0.3, 0.6, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="text-gray-500 text-sm"
                            >
                              Connecting to SweatRoom...
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Live News */}
                  <div className="flex-1 min-h-0">
                    <div className={`h-full bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-2xl border overflow-hidden flex flex-col transition-all duration-300 ${
                      highlightElement === "news" ? "border-orange-500/50 shadow-xl shadow-orange-500/20 ring-2 ring-orange-500/30" : "border-orange-500/20"
                    }`}>
                      {/* Inline Feature Badge for News */}
                      <AnimatePresence>
                        {stage === "highlight_news" && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="px-3 py-2.5 bg-gradient-to-r from-orange-500/20 to-red-500/20 border-b border-orange-500/30"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <motion.div
                                  animate={{ scale: [1, 1.1, 1] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                  className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center"
                                >
                                  <Newspaper size={16} className="text-orange-400" />
                                </motion.div>
                                <div>
                                  <p className="text-orange-400 font-bold text-xs">Live News & Updates</p>
                                  <p className="text-gray-400 text-[10px]">Breaking news filtered to YOUR picks</p>
                                </div>
                              </div>
                              <motion.button
                                onClick={handleHighlightContinue}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-3 py-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1"
                              >
                                Continue <ArrowRight size={12} />
                              </motion.button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="p-3 border-b border-white/10 bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Newspaper size={16} className="text-orange-400" />
                            <span className="font-semibold text-white text-sm">Live News</span>
                          </div>
                          <motion.span
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                            className="text-orange-400"
                          >
                            <Zap size={14} />
                          </motion.span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
                        <AnimatePresence>
                          {newsHeadlines.map((news, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 20, scale: 0.9 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className="bg-white/5 rounded-lg p-2"
                            >
                              <div className="flex items-start gap-2">
                                <TrendingUp size={14} className="text-orange-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-300 text-sm">{news.headline}</span>
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {newsHeadlines.length === 0 && (
                          <div className="flex items-center justify-center h-full">
                            <motion.div
                              animate={{ opacity: [0.3, 0.6, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="text-gray-500 text-sm"
                            >
                              Loading updates...
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Community Feed View - available after tutorial */}
        {isLivePhase && simulatorView === "community" && (
          <CommunityFeedSimulator
            onClose={handleClose}
            onSwitchView={handleSwitchView}
          />
        )}

        {/* Join Early Screen */}
        {stage === "joinEarly" && (
          <motion.div
            key="joinEarly"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md bg-[#0A0A0A] border border-primary/30 rounded-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-primary/20 rounded-full blur-[100px] pointer-events-none" />

              <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
              >
                <X size={20} />
              </button>

              <div className="relative p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="inline-flex items-center gap-2 mb-6"
                >
                  <Logo size="md" variant="icon-only" forceTheme="dark" />
                  <span className="text-2xl font-bold text-primary">PARLAY</span>
                  <span className="text-2xl font-bold text-white">GROUND</span>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 border border-primary/30 rounded-full mb-4"
                >
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                    Early Access
                  </span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-3xl font-black text-white mb-2"
                >
                  That's ParlayGround
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-gray-400 mb-6"
                >
                  Join now to track your picks, build your reputation, and connect with sports fans worldwide.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="grid grid-cols-2 gap-3 mb-6"
                >
                  {[
                    { icon: Flame, label: "SweatRooms" },
                    { icon: Sparkles, label: "GroundTokens" },
                    { icon: Trophy, label: "Leaderboards" },
                    { icon: Users, label: "Community" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10"
                    >
                      <item.icon size={18} className="text-primary" />
                      <span className="text-white text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="p-4 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 mb-6"
                >
                  <div className="flex items-center justify-center gap-2 text-primary font-bold mb-1">
                    <Crown size={18} />
                    Founding Member Bonus
                  </div>
                  <p className="text-white text-sm">
                    Sign up now and receive <span className="text-primary font-black">100 GT</span> + exclusive OG tag
                  </p>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  onClick={handleJoinEarly}
                  className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_40px_rgba(34,197,94,0.6)] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Join Early Access
                  <ArrowRight size={20} />
                </motion.button>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="text-gray-500 text-xs mt-4"
                >
                  Free forever. No credit card required.
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DemoPage;
